import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { create } from 'zustand';
import _ from 'lodash';

import { initialState } from '@modules/activities/store/store.entity';

import { encryptJSONStorage } from '@core/lib/encrypt-json-storage';

import { AuthStore, initialAuthState } from './store.entity';
import { fetchLoginService, sendCheckIn, sendCheckOut } from './store.api';

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      immer((set, get) => ({
        ...initialState,
        setUser(user) {
          set((store) => {
            store.user = user;
          });
        },
        setAccessToken(token) {
          set((store) => {
            store.accessToken = token;
          });
        },
        async login({ document, password }) {
          try {
            set((state) => {
              state.loading = true;
            });
            const {
              data: { user },
              token,
            } = await fetchLoginService({
              document,
              password,
            });

            set((state) => {
              state.user = {
                ...user,
                lastLogin: new Date(),
              };
              state.accessToken = token;
            });

            return user;
          } catch (error: any) {
            throw new Error(error);
          } finally {
            set((state) => {
              state.loading = false;
            });
          }
        },
        async checkin(campaignId, checkinData) {
          try {
            set((state) => {
              state.loading = true;
            });
            const { status, data } = await sendCheckIn(campaignId, checkinData);
            if (status === 200) {
              set((state) => {
                state.user!.validateCheckIn = true;
                state.user!.activeCheckin = {
                  id: data.data.id,
                  campaign: data.data.campaign_id,
                  latitude: checkinData.latitude,
                  longitude: checkinData.longitude,
                };
              });
            }
          } catch (error: any) {
            throw new Error(error);
          } finally {
            set((state) => {
              state.loading = false;
            });
          }
        },
        async checkout() {
          try {
            set((state) => {
              state.loading = true;
            });
            
            // Obtener el ID del check-in activo
            const activeCheckinId = get().user?.activeCheckin?.id;
            
            if (!activeCheckinId) {
              throw new Error('No hay check-in activo');
            }

            // Hacer la petición de checkout al backend
            await sendCheckOut(activeCheckinId);
            
            // Actualizar el estado local
            set((state) => {
              state.user!.activeCheckin = undefined;
              state.user!.validateCheckIn = false;
            });
          } catch (error: any) {
            throw new Error(error);
          } finally {
            set((state) => {
              state.loading = false;
            });
          }
        },
        reset() {
          set(() => initialAuthState);
        },
      })),
      {
        name: 'auth-s',
        storage: encryptJSONStorage(),
      }
    )
  )
);
